import * as React from 'react';
import { SIDEBAR_AUTO_COLLAPSE_WIDTH } from '../../theme/breakpoints';

// IB-1885: the desktop sidebar takes 250px, which on a laptop squeezes tables until their action
// buttons spill over. Below SIDEBAR_AUTO_COLLAPSE_WIDTH it starts collapsed to its 60px icon rail;
// once a user toggles it, their choice is remembered for this browser instead.
const DRAWER_STORAGE_KEY = 'ib-sidebar-open';

const getInitialDrawerOpen = (): boolean => {
  try {
    const stored = window.localStorage.getItem(DRAWER_STORAGE_KEY);
    if (stored === 'true' || stored === 'false') {
      return stored === 'true';
    }
  } catch (e) {
    // storage can be unavailable (private mode, blocked site data); fall back to the screen size
  }
  return window.innerWidth >= SIDEBAR_AUTO_COLLAPSE_WIDTH;
};

type Props = {
  children?: JSX.Element;
};

type FilterState = {
  districtId?: string;
  ministryId?: string;
  emisNumber?: string;
  filterType?: string;
  name?: string;
  provinceId?: string;
  status?: string;
  typeId?: string;
};

type State = {
  modalOpen: boolean;
  drawerOpen: boolean;
  openMobileDrawer: boolean;
  filterState: FilterState;
  flatFileLicenseKey: string;
  closeModal: () => void;
  toggleDrawer: () => void;
  toggleMobileDrawer: () => void;
  setFilterState: (state: FilterState) => void;
  setFlatFileLicenseKey: (key: string) => void;
};

const defaultValues = {
  modalOpen: false,
  drawerOpen: true,
  openMobileDrawer: false,
  filterState: {
    districtId: undefined,
    ministryId: undefined,
    emisNumber: undefined,
    filterType: undefined,
    name: undefined,
    provinceId: undefined,
    status: undefined,
    typeId: undefined,
  },
  flatFileLicenseKey: '',
  closeModal: () => null,
  toggleDrawer: () => null,
  toggleMobileDrawer: () => null,
  setFilterState: () => null,
  setFlatFileLicenseKey: () => null,
};

export const GlobalAppContext = React.createContext<State>(defaultValues);

class ContextProvider extends React.Component<Props, State> {
  state: State = {
    modalOpen: true,
    drawerOpen: getInitialDrawerOpen(),
    openMobileDrawer: false,
    filterState: {
      districtId: undefined,
      ministryId: undefined,
      emisNumber: undefined,
      filterType: undefined,
      name: undefined,
      provinceId: undefined,
      status: undefined,
      typeId: undefined,
    },
    flatFileLicenseKey: '',
    closeModal: () => this._closeModal(),
    toggleDrawer: () => this._toggleDrawer(),
    toggleMobileDrawer: () => this._toggleMobileDrawer(),
    setFilterState: filterState => this._setFilterState(filterState),
    setFlatFileLicenseKey: flatFileLicenseKey => this._setFlatFileLicenseKey(flatFileLicenseKey),
  };

  _toggleDrawer() {
    const drawerOpen = !this.state.drawerOpen;
    this.setState({ drawerOpen });
    try {
      window.localStorage.setItem(DRAWER_STORAGE_KEY, String(drawerOpen));
    } catch (e) {
      // not remembering the choice is harmless
    }
  }

  _toggleMobileDrawer() {
    this.setState({ openMobileDrawer: !this.state.openMobileDrawer });
  }

  _closeModal() {
    this.setState({ modalOpen: false });
  }

  _setFilterState(filterState: FilterState) {
    this.setState({ filterState });
  }

  _setFlatFileLicenseKey(flatFileLicenseKey: string) {
    this.setState({ flatFileLicenseKey: flatFileLicenseKey });
  }

  render() {
    return <GlobalAppContext.Provider value={this.state}>{this.props.children}</GlobalAppContext.Provider>;
  }
}

export default ContextProvider;
